import React, { ComponentProps, useState } from 'react';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { DefaultView, EditView, Views } from '../../types/BlockWithViews';
import { UserToPageRolePageLeadEditView } from './partials/UserToPageRolePageLeadEditView';
import { BlockBar, BlockBarAnchor, useBlockBarCreateHref } from '../../_lib/components/BlockBar';
import { Icons } from '../../components/atoms/SVG';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { PageLeadRole } from '../../_lib/components/PageLead/PageLeadRole';

type UserToPageRolePageLead = Views<DefaultView<{
  name: string;
  description?: string;
}> & EditView<any>>;

function UserToPageRolePageLead(props: UserToPageRolePageLead) {
  const { t } = useSonTranslation('RolePageLead');
  const { active } = useAppContext().editModeInfo;
  const { refresh } = useRefreshBlock();
  const id = useHtmlValidId();
  const { elementType } = useBlockContext<BlockType.usertouserrolepagelead | BlockType.usertopagerolepagelead>();
  const [currentActiveView, setCurrentActiveView] = useState('default');
  const currentActiveViewHref = useBlockBarCreateHref(
    currentActiveView,
    { default: props.views.edit?.href, edit: props.views.default?.href },
    id,
  );

  return (
    <>
      {active
        ? <>
            <BlockBar heading={t(`${elementType}.blockBarHeading`)} hierarchy={2}>
              {props.views.edit
                ? (
                    <BlockBarAnchor
                      text={t(`${elementType}.editTitle`)}
                      svgSpriteId={currentActiveView === 'edit' ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
                      aria-controls={id}
                      aria-expanded={currentActiveView === 'edit'}
                      href={currentActiveViewHref}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.stopPropagation();
                        event.preventDefault();

                        if (currentActiveView === 'default') {
                          setCurrentActiveView('edit');
                        } else if (currentActiveView === 'edit') {
                          setCurrentActiveView('default');
                          refresh();
                        }
                      }}
                    />
                  )
                : null}
            </BlockBar>

            <div id={id} className="role-page-lead">
              {currentActiveView === 'default'
                ? <PageLeadRole
                    title={props.views.default.embedded.name}
                    leadText={props.views.default.embedded.description}
                    elementType={elementType}
                  />
                : null}
              {currentActiveView === 'edit'
                ? <UserToPageRolePageLeadEditView {...props.views.edit} />
                : null}
            </div>
          </>
        : <PageLeadRole
            title={props.views.default.embedded.name}
            leadText={props.views.default.embedded.description}
            elementType={elementType}
          />}
    </>
  );
}

export const UserToPageRolePageLeadBlock = asFetchableBlock(UserToPageRolePageLead, BlockType.usertopagerolepagelead);
export type UserToPageRolePageLeadBlock = ComponentProps<typeof UserToPageRolePageLeadBlock>;
